import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'next-i18next/pages';

import { Form, notification } from 'antd';

import { leads } from '@/api/amo-crm/leads';
import { sendCalculatorFormData } from '@/api/calculator/send-calculator-info';
import { CenteredLoaderWithBlur } from '@/components/common';
import { Environment } from '@/constants/environments';
import { TranslationNamespace } from '@/i18n';
import {
	ymOnCalculatorComplete,
	ymOnCalculatorOpen,
	ymOnCalculatorStepDone,
	ymOnDemoRequestSuccess,
} from '@/metrics';
import { getCurrentSessionUtm } from '@/utils';

import { CONTACT_FIELDS, DEFAULT_FORM_VALUES, type FormValues, type YesNo } from '../../constants';
import { calculate, type CalculationResult } from '../../lib';
import { ChartModal } from '../chart/ChartModal';
import { Results } from '../results/Results';

import { StepCompany } from './steps/StepCompany';
import { StepCosts } from './steps/StepCosts';
import { StepFeatures } from './steps/StepFeatures';
import { StepTime } from './steps/StepTime';
import { buildRequestData, toCalculatorInput } from './Calculator.utils';
import { StepProgress } from './StepProgress';

import styles from '@/components/TariffCalculatorNew/TariffCalculator.module.scss';

export const getSourceForCalculateCRM = (environment: Environment) => {
	switch (environment) {
		case Environment.Prod:
			return 'Калькуляция с прода';
		case Environment.Demo:
			return 'Калькуляция с демо';
		case Environment.Test:
			return 'Калькуляция с теста';
		default:
			return;
	}
};

export function Calculator() {
	const { t } = useTranslation(TranslationNamespace.Calculator);
	const [form] = Form.useForm<FormValues>();
	const [step, setStep] = useState(0);
	const [contactError, setContactError] = useState('');
	const [result, setResult] = useState<CalculationResult | null>(null);
	const [companyName, setCompanyName] = useState('');
	const [chartOpen, setChartOpen] = useState(false);
	const shellRef = useRef<HTMLDivElement>(null);
	const chartTimerRef = useRef<number | null>(null);
	const [isSendingData, setIsSendingData] = useState(false);

	useEffect(() => {
		ymOnCalculatorOpen();
	}, []);

	useEffect(() => {
		if (result) {
			ymOnCalculatorComplete();
		}
	}, [result]);

	useEffect(() => {
		return () => {
			if (chartTimerRef.current != null) window.clearTimeout(chartTimerRef.current);
		};
	}, []);

	const hasDeferred = Form.useWatch('hasDeferred', form) as YesNo | undefined;
	const hasSite = Form.useWatch('hasSite', form) as YesNo | undefined;
	const hasMarketer = Form.useWatch('hasMarketer', form) as YesNo | undefined;

	const scrollToShell = () => {
		shellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const goToStep = (index: number) => {
		setStep(Math.max(0, Math.min(3, index)));
		scrollToShell();
	};

	const handleNextFromContacts = async () => {
		try {
			await form.validateFields([...CONTACT_FIELDS]);
			setContactError('');
			ymOnCalculatorStepDone(1);
			goToStep(1);
		} catch {
			setContactError(t('validation.contacts'));
		}
	};

	const handleFinish = async () => {
		const values = form.getFieldsValue(true) as FormValues;
		const company = String(values.company ?? '').trim();
		const name = String(values.name ?? '').trim();
		const phone = String(values.phone ?? '').trim();

		if (company.length <= 1 || name.length <= 1 || phone.length <= 1) {
			setContactError(t('validation.contacts'));
			goToStep(0);
			return;
		}

		setContactError('');
		setIsSendingData(true);
		const calculation = calculate(toCalculatorInput({ ...values, company, name, phone }));
		const requestData = buildRequestData({ ...values, company, name, phone }, calculation);
		ymOnCalculatorStepDone(4);
		try {
			const utm = getCurrentSessionUtm(window.location.href);
			await sendCalculatorFormData(requestData);

			const crmErrors = await leads({
				company,
				phone: `+${phone.replaceAll('+', '')}`,
				name,
				source: getSourceForCalculateCRM(process.env.ENVIRONMENT as Environment),
				...utm,
			});

			if (crmErrors === null) {
				ymOnDemoRequestSuccess();
			}

			notification.success({
				title: t('notification.complete'),
				description: t('notification.complete-description'),
			});
		} catch {
			console.error('Failed to send calculator form data');
		} finally {
			setIsSendingData(false);
		}

		setCompanyName(company);
		setResult(calculation);
		scrollToShell();
		if (chartTimerRef.current != null) window.clearTimeout(chartTimerRef.current);
		chartTimerRef.current = window.setTimeout(() => setChartOpen(true), 450);
	};

	const handleEdit = () => {
		if (chartTimerRef.current !== null) window.clearTimeout(chartTimerRef.current);
		setChartOpen(false);
		setResult(null);
		goToStep(0);
	};

	return (
		<div className={styles.calculatorShell} id="calculator" ref={shellRef}>
			{isSendingData && <CenteredLoaderWithBlur isLoading={isSendingData} />}
			{!result && <StepProgress step={step} />}

			{result ? (
				<>
					<Results
						companyName={companyName}
						result={result}
						onEdit={handleEdit}
						onShowChart={() => setChartOpen(true)}
					/>
					<ChartModal open={chartOpen} result={result} onClose={() => setChartOpen(false)} />
				</>
			) : (
				<Form
					form={form}
					layout="vertical"
					initialValues={{ ...DEFAULT_FORM_VALUES, company: t('company.sample') }}
					requiredMark={false}
					preserve
					onFinish={handleFinish}
					onFinishFailed={() => {
						setContactError(t('validation.contacts'));
						goToStep(0);
					}}
					className={styles.formBody}
				>
					{step === 0 && (
						<>
							<StepCompany onNext={handleNextFromContacts} />
							{contactError && (
								<div className={styles.formError} role="alert" aria-live="assertive">
									{contactError}
								</div>
							)}
						</>
					)}
					{step === 1 && (
						<StepTime
							onPrev={() => goToStep(0)}
							onNext={() => {
								ymOnCalculatorStepDone(2);
								goToStep(2);
							}}
						/>
					)}
					{step === 2 && (
						<StepCosts
							onPrev={() => goToStep(1)}
							onNext={() => {
								ymOnCalculatorStepDone(3);
								goToStep(3);
							}}
							hasDeferred={hasDeferred ?? 'yes'}
							hasSite={hasSite ?? 'yes'}
							hasMarketer={hasMarketer ?? 'no'}
						/>
					)}
					{step === 3 && <StepFeatures onPrev={() => goToStep(2)} />}
				</Form>
			)}
		</div>
	);
}
